import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Mail, Phone, MapPin, ChefHat, Users, Clock, Sparkles, BookOpen, Languages, Award } from 'lucide-react';
import './Portfolio.css';

const Portfolio = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const galleryRef = useRef(null);

  // ============================================
  // GALLERY IMAGES - ADD YOUR CUSTOM IMAGE URLS HERE
  // ============================================
  // Replace these placeholder URLs with your own culinary work images
  // You can use online image URLs or local paths like '/gallery/image1.jpg'
  const galleryImages = [
    '/images/473544671_1548809905846155_7561321417897836423_n.jpg', // Add your image URL here
    '/images/475032118_1556917398368739_6839258293825614510_n.jpg', // Add your image URL here
    '/images/480250506_1569832880410524_2370160887265098092_n.jpg', // Add your image URL here
    '/images/482191180_1585874862139659_8823611215676946012_n.jpg', // Add your image URL here
    '/images/482359442_1586455602081585_619751061081685883_n.jpg', // Add your image URL here
    '/images/483364270_1585969438796868_7526574681609180306_n.jpg', // Add your image URL here
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  // Auto-scroll gallery effect
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    let scrollPosition = 0;
    const scrollSpeed = 1; // Change this value to adjust scroll speed (0.5 = slower, 2 = faster)
    
    const autoScroll = () => {
      if (gallery) {
        scrollPosition += scrollSpeed;
        
        // Reset to beginning when reaching the end for seamless loop
        if (scrollPosition >= gallery.scrollWidth - gallery.clientWidth) {
          scrollPosition = 0;
        }
        
        gallery.scrollLeft = scrollPosition;
      }
    };

    const intervalId = setInterval(autoScroll, 30);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="portfolio">
      {/* Navigation */}
      <nav className="navigation">
        <div className="nav-container">
          <div className="nav-logo">Projuti Baidya</div>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <li><button onClick={() => scrollToSection('about')}>About</button></li>
            <li><button onClick={() => scrollToSection('gallery')}>Gallery</button></li>
            <li><button onClick={() => scrollToSection('skills')}>Skills</button></li>
            <li><button onClick={() => scrollToSection('experience')}>Experience</button></li>
            <li><button onClick={() => scrollToSection('education')}>Education</button></li>
            <li><button onClick={() => scrollToSection('contact')}>Contact</button></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" id="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Projuti Baidya</h1>
          <p className="hero-tagline">Aspiring Culinary Professional</p>
          <p className="hero-description">
            Reliable and organized kitchen professional with experience in food service,
            passionate about developing my career in Finland's culinary industry.
          </p>
          <button className="btn-primary" onClick={() => scrollToSection('contact')}>
            Get In Touch
          </button>
        </div>
      </section>

      {/* About Section */}
      <section className="section about-section" id="about">
        <div className="container">
          <div className="grid-two-column">
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?w=800&q=80" 
                alt="Professional kitchen environment"
              />
            </div>
            <div className="about-content">
              <h2 className="section-title">About Me</h2>
              <div className="about-text">
                <p>
                  I am a reliable and organized individual with hands-on experience in the food service 
                  industry. My goal is to develop my career in Finland, applying my skills, gaining 
                  international experience, and contributing positively to a professional kitchen team.
                </p>
                <p>
                  I have a lively, self-initiative attitude and am a good listener and communicator. 
                  Through my experience at Bean and Leaf in Dhaka, I've developed strong skills in 
                  efficiency during rush hours, maintaining quality under pressure, and understanding 
                  the critical importance of teamwork in a culinary environment.
                </p>
                <p>
                  Currently based in Tampere, Finland, I am actively learning Finnish and eager to 
                  integrate into the local culinary scene while bringing my international training 
                  and passion for food preparation.
                </p>
              </div>
              <div className="location-info">
                <MapPin size={20} />
                <span>Tampere, Finland</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section gallery-section" id="gallery">
        <div className="container">
          <h2 className="section-title centered">My Bakery Journey</h2>
          <p className="gallery-subtitle">A visual showcase of my baking creations.</p>
          
          <div className="gallery-container" ref={galleryRef}>
            <div className="gallery-track">
              {/* First set of images */}
              {galleryImages.map((imageUrl, index) => (
                <div key={index} className="gallery-item">
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={`Culinary work ${index + 1}`}
                      className="gallery-image"
                    />
                  ) : (
                    <div className="gallery-image-placeholder">
                      <ChefHat size={48} />
                      <p>Image {index + 1}</p>
                    </div>
                  )}
                </div>
              ))}
              {/* Duplicate for seamless loop */}
              {galleryImages.map((imageUrl, index) => (
                <div key={`dup-${index}`} className="gallery-item">
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={`Culinary work ${index + 1}`}
                      className="gallery-image"
                    />
                  ) : (
                    <div className="gallery-image-placeholder">
                      <ChefHat size={48} />
                      <p>Image {index + 1}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="section skills-section" id="skills">
        <div className="container">
          <h2 className="section-title centered">Skills & Competencies</h2>
          
          <div className="skills-category">
            <h3 className="skills-subtitle">Technical Skills</h3>
            <div className="skills-grid">
              <div className="skill-card">
                <ChefHat size={32} />
                <h4>Food Preparation</h4>
                <p>Washing, chopping, portioning ingredients with precision</p>
              </div>
              <div className="skill-card">
                <Sparkles size={32} />
                <h4>Preparation & Presentation</h4>
                <p>Professional plating and dish presentation techniques</p>
              </div>
              <div className="skill-card">
                <Award size={32} />
                <h4>Bakery & Patisserie</h4>
                <p>Professional baking and pastry preparation</p>
              </div>
              <div className="skill-card">
                <BookOpen size={32} />
                <h4>Store Kitchen Supplies</h4>
                <p>Proper food storage and inventory management</p>
              </div>
            </div>
          </div>

          <div className="skills-category">
            <h3 className="skills-subtitle">Professional Skills</h3>
            <div className="skills-grid">
              <div className="skill-card">
                <Sparkles size={32} />
                <h4>Hygiene & Cleanliness</h4>
                <p>HACCP certified, Finnish Hygiene Passport holder</p>
              </div>
              <div className="skill-card">
                <Users size={32} />
                <h4>Teamwork</h4>
                <p>Collaborative approach in fast-paced kitchen environments</p>
              </div>
              <div className="skill-card">
                <Clock size={32} />
                <h4>Time Management</h4>
                <p>Efficient task completion during peak hours</p>
              </div>
              <div className="skill-card">
                <Users size={32} />
                <h4>Customer Service</h4>
                <p>Supporting serving staff and maintaining quality standards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="section experience-section" id="experience">
        <div className="container">
          <h2 className="section-title centered">Work Experience</h2>
          
          <div className="experience-card">
            <div className="experience-header">
              <div>
                <h3 className="experience-title">Kitchen Worker</h3>
                <p className="experience-company">Bean and Leaf</p>
                <p className="experience-location">Dhaka, Bangladesh</p>
              </div>
              <p className="experience-date">April 2023 - October 2024</p>
            </div>
            
            <div className="experience-content">
              <h4>Responsibilities:</h4>
              <ul className="experience-list">
                <li>Assisted chefs with daily food preparation including washing, chopping, and portioning ingredients</li>
                <li>Maintained high standards of kitchen cleanliness and hygiene in accordance with food safety protocols</li>
                <li>Operated dishwashers and ensured all utensils and equipment were properly cleaned and stored</li>
                <li>Supported serving staff during peak hours to ensure smooth service operations</li>
                <li>Managed food storage areas and monitored inventory levels</li>
              </ul>

              <h4>Key Learnings:</h4>
              <ul className="experience-list">
                <li>Developed efficiency and composure during rush hours in a high-pressure environment</li>
                <li>Ensured consistent quality standards under time constraints</li>
                <li>Understood the critical importance of teamwork and communication in a professional kitchen</li>
                <li>Learned to anticipate needs and work proactively to support the culinary team</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section className="section education-section" id="education">
        <div className="container">
          <h2 className="section-title centered">Education & Certifications</h2>
          
          <div className="education-grid">
            <div className="education-card">
              <div className="education-icon">
                <Award size={32} />
              </div>
              <h3>Professional Cooking Academy</h3>
              <p className="education-org">City and Guilds</p>
              <p className="education-location">Dhaka, Bangladesh</p>
              <p className="education-date">February - May 2025</p>
            </div>

            <div className="education-card">
              <div className="education-icon">
                <Award size={32} />
              </div>
              <h3>Level III Certificate</h3>
              <p className="education-org">Professional Bakery & Patisserie</p>
              <p className="education-sub">International Training Institute of Culinary Arts (ITICA)</p>
              <p className="education-date">September - December 2024</p>
            </div>

            <div className="education-card">
              <div className="education-icon">
                <Award size={32} />
              </div>
              <h3>HACCP & Hygiene Training</h3>
              <p className="education-org">SGS International</p>
              <p className="education-location">Dhaka, Bangladesh</p>
              <p className="education-date">May 2024</p>
            </div>

            <div className="education-card">
              <div className="education-icon">
                <Award size={32} />
              </div>
              <h3>Skills Proficiency Certificate</h3>
              <p className="education-org">Basic Food Preparation</p>
              <p className="education-sub">City & Guilds (Qualification Number 3529-50)</p>
              <p className="education-date">Certified</p>
            </div>

            <div className="education-card highlight">
              <div className="education-icon">
                <Award size={32} />
              </div>
              <h3>Finnish Hygiene Passport</h3>
              <p className="education-org">Required for Food Handling in Finland</p>
              <p className="education-date">Certified</p>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="section languages-section">
        <div className="container">
          <h2 className="section-title centered">Language Skills</h2>
          
          <div className="languages-grid">
            <div className="language-card">
              <Languages size={32} />
              <h3>Bengali</h3>
              <p className="language-level">Mother Tongue</p>
              <p className="language-description">Native proficiency</p>
            </div>

            <div className="language-card">
              <Languages size={32} />
              <h3>English</h3>
              <p className="language-level">Proficient User (C1/C2)</p>
              <p className="language-description">Advanced professional proficiency</p>
            </div>

            <div className="language-card">
              <Languages size={32} />
              <h3>Hindi</h3>
              <p className="language-level">Proficient Speaker (C2)</p>
              <p className="language-description">Fluent speaking, basic reading (A2)</p>
            </div>

            <div className="language-card learning">
              <Languages size={32} />
              <h3>Finnish</h3>
              <p className="language-level">Basic User (A1)</p>
              <p className="language-description">Currently Learning</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer className="contact-section" id="contact">
        <div className="container">
          <h2 className="section-title centered">Get In Touch</h2>
          <p className="contact-intro">
            I'm currently seeking opportunities in professional kitchens in Finland. 
            If you're looking for a reliable, organized, and passionate culinary professional, 
            I'd love to hear from you.
          </p>
          
          <div className="contact-grid">
            <div className="contact-card">
              <Mail size={32} />
              <h3>Email</h3>
              <a href="mailto:baidya.projuti987@gmail.com">baidya.projuti987@gmail.com</a>
            </div>

            <div className="contact-card">
              <Phone size={32} />
              <h3>Phone</h3>
              <a href="tel:+358449308331">(+358) 449308331</a>
            </div>

            <div className="contact-card">
              <MapPin size={32} />
              <h3>Location</h3>
              <p>Tampere, Finland</p>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 Projuti Baidya. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;